import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Hold in-app navigation while a page has unsaved work.
 *
 * react-router's `useBlocker` only functions under a data router (`createBrowserRouter`). This app
 * mounts `<BrowserRouter>` with its route tree inside Amplify's `<Authenticator>` render prop, which
 * can't be expressed as a data-router route tree without restructuring auth — so blocking is done
 * directly: a capture-phase click listener catches in-app link clicks before the router sees them,
 * and `beforeunload` covers reload, tab close, and navigation off-site.
 *
 * Known gap: the browser Back button is not intercepted. Doing so means pushing sentinel history
 * entries and unwinding them on popstate, which leaves the history stack in a worse state than the
 * warning is worth.
 */
export function useUnsavedGuard(dirty: boolean) {
  const navigate = useNavigate();
  const [pending, setPending] = useState<string | null>(null);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ''; // required by Chrome/Safari to show the native prompt
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return;
    const onClick = (e: MouseEvent) => {
      // Leave modified clicks alone — they open a new tab and don't unload this one.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as HTMLElement | null)?.closest?.('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || anchor.target === '_blank') return;
      // Only in-app routes; external/mailto/tel navigation is covered by beforeunload.
      if (!href.startsWith('/')) return;
      if (href === window.location.pathname) return;
      e.preventDefault();
      e.stopPropagation();
      setPending(href);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [dirty]);

  return {
    /** Destination the operator tried to reach, while the navigation is held. */
    pending,
    /** Abandon the held navigation and stay put. */
    cancel: () => setPending(null),
    /** Continue to the held destination — call after saving, or when discarding deliberately. */
    proceed: () => {
      const to = pending;
      setPending(null);
      if (to) navigate(to);
    },
  };
}
